import { Object } from '../Object/Object'

import validateEventName from './utils/validateEventName'
import type { IEvent, TCallback, IEventHandler } from './EventHandler'
import BaseEventHandler from './EventHandler'

export interface IGameEventHandler extends IEventHandler {
  readonly clear: (eventName?: string, isIncludes?: boolean) => void
}

export default class GameEventHandler extends BaseEventHandler implements IGameEventHandler {
  public get gameEvents (): readonly string[] {
    return Object.getKeys(this.events).filter((ev) => ev.startsWith('Game:'))
  }

  /**
   * Egy esemény hallgatása.
   * @param eventName - Az esemény.
   * @param callback  - A meghívandó callback.
   * @throws            Ha nem Game kezdetű esemény, akkor hibát dob.
   */
  public override on (eventName: string, callback: TCallback): void {
    validateEventName(eventName)

    super.on(eventName, callback)
  }

  /**
   * Egy esemény egyszeri hallgatása.
   * Meghívás után automatikusan leiratkozik.
   * @param eventName - Az esemény.
   * @param callback  - A meghívandó callback.
   * @throws            Ha nem Game kezdetű esemény, akkor hibát dob.
   */
  public override once (eventName: string, callback: TCallback): void {
    validateEventName(eventName)

    super.once(eventName, callback)
  }

  /**
   * Egy eseményről leiratkozás.
   * @param eventName - Az esemény.
   * @param callback  - A meghívandó callback.
   * @throws            Ha nem Game kezdetű esemény, akkor hibát dob.
   */
  public override off (eventName: string, callback: TCallback): void {
    validateEventName(eventName)

    super.off(eventName, callback)
  }

  /**
   * Egy esemény kiváltása.
   * @param eventName - Az esemény.
   * @param event     - Az event object, amit küldünk. Opcionális.
   * @throws            Ha nem Game kezdetű esemény, akkor hibát dob.
   */
  public override emit (eventName: string, event?: IEvent): Promise<void[]> {
    validateEventName(eventName)

    return super.emit(eventName, event)
  }

  /**
   * Vagy egy eventet törlünk, vagy az összeset.
   * @param eventName  - Az esemény.
   * @param isIncludes - Az összes olyan eseményről le kell iratkozni amiben benne van az `eventName`?
   * @throws             Ha nem Game kezdetű esemény, akkor hibát dob.
   */
  public override clear (eventName?: string, isIncludes = false): void {
    if (eventName) {
      // Elég, ha csak benne szerepel.
      if (isIncludes) {
        this.gameEvents.forEach((ev) => {
          if (ev.includes(eventName)) {
            super.clear(ev)
          }
        })
      }
      // Teljes egyezéssel jó csak.
      else if (eventName.startsWith('Game:')) {
        super.clear(eventName)
      }
      else {
        throw new SyntaxError('[GameEventHandler] Csak a Game namespace eventeket lehet törölni!')
      }
    }
    // Ha nincs semmi, akkor teljes törlés
    else {
      this.logger.dev('clear:', this.gameEvents.join(', '))

      this.gameEvents.forEach((ev) => {
        super.clear(ev)
      })
    }
  }
}
